import { useParams } from "react-router-dom"
import Layout from "@/components/Layout/Layout"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { LoadingScreen } from "@/components/ui/loading-spinner"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Trophy, Play, AlertCircle, Trash2, Users, Table2, Search } from "lucide-react"
import { HeaderTournament, TournamentNavigation } from "@/components/TournamentNavigation"
import { useTournamentRound } from "@/graphql/hooks/useTournamentRound"
import { useRoundStart } from "@/graphql/hooks/useRoundStart"
import { useRoundReset } from "@/graphql/hooks/useRoundReset"
import { useTournamentMatchUpdate } from "@/graphql/hooks/useTournamentMatchUpdate"
import { useTournamentMatchCreate } from "@/graphql/hooks/useTournamentMatchCreate"
import { MatchTable } from "@/components/MatchTable"
import { MatchReportModal } from "@/components/MatchReportModal"
import { MatchTablePrint } from "@/components/MatchTablePrint"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/hooks/use-toast"
import { useState, useMemo } from "react"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { UserAvatar } from "@/components/ui/user-avatar"
import { Input } from "@/components/ui/input"
import { useAuthContext } from "@/contexts/AuthContext"
import { useTournamentInfo, useTournamentOrganizers, useTournamentPlayers } from "@/graphql"
import { useTournamentPlayerByCode } from "@/graphql/hooks/useTournamentPlayerByCode"

// Id de jugador (Person.id) resuelto por código y guardado en este
// navegador, por torneo, para no tener que volver a preguntar el código en
// cada ronda del mismo torneo. El código en sí nunca se guarda ni se
// muestra: solo se usa una vez contra el backend para resolver el id.
const MY_TOURNAMENT_PLAYER_STORAGE_KEY = "myTournamentPlayerByTournament"

/**
 * Lee el mapa { [tournamentId]: personId } guardado en este navegador.
 */
const readStoredPlayerMap = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(MY_TOURNAMENT_PLAYER_STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const getStoredPersonId = (tournamentId: number): number | null => {
  const map = readStoredPlayerMap()
  return map[String(tournamentId)] ?? null
}

const setStoredPersonId = (tournamentId: number, personId: number) => {
  const map = readStoredPlayerMap()
  map[String(tournamentId)] = personId
  localStorage.setItem(MY_TOURNAMENT_PLAYER_STORAGE_KEY, JSON.stringify(map))
}

const clearStoredPersonId = (tournamentId: number) => {
  const map = readStoredPlayerMap()
  delete map[String(tournamentId)]
  localStorage.setItem(MY_TOURNAMENT_PLAYER_STORAGE_KEY, JSON.stringify(map))
}

// Formato del código de jugador: 2 letras + dígitos (ej. "CL001"), sin
// espacios y en mayúsculas.
const PLAYER_CODE_PATTERN = /^[A-Z]{2}\d+$/

/**
 * Normaliza lo que se va escribiendo: mayúsculas y sin espacios, para que
 * el usuario vea de inmediato el formato esperado mientras tipea.
 */
const normalizePlayerCodeInput = (value: string) => value.toUpperCase().replace(/\s+/g, "")

const TournamentRoundView = () => {
  const { roundId, slug, pdf, tables, tournamentId } = useParams<{
    roundId: string
    slug: string
    pdf?: string
    tables?: string
    tournamentId?: string
  }>()
  const { toast } = useToast()
  const { userInfo, isAdmin, isAuthenticated } = useAuthContext()

  const { round, loading, error, refetch: refetchRound } = useTournamentRound(Number(roundId))

  const { tournament, loading: loadingTournament } = useTournamentInfo(Number(tournamentId))
  const { organizers, loading: loadingOrganizers } = useTournamentOrganizers(
    Number(tournamentId),
    isAuthenticated,
  )

  const { roundStart, loading: startingRound } = useRoundStart()
  const { roundReset, loading: resettingRound } = useRoundReset()
  const { tournamentMatchUpdate, loading: updatingMatchTables } = useTournamentMatchUpdate()
  const { tournamentMatchCreate, loading: creatingMatchTable } = useTournamentMatchCreate()
  const { players: tournamentPlayers } = useTournamentPlayers(Number(tournamentId))
  const { findPlayerByCode, loading: findingPlayerByCode } = useTournamentPlayerByCode()
  const [showOnlyPending, setShowOnlyPending] = useState(false)
  const [selectedMatch, setSelectedMatch] = useState(null)
  const [showResetConfirmDialog, setShowResetConfirmDialog] = useState(false)
  const [showResetSecondConfirmDialog, setShowResetSecondConfirmDialog] = useState(false)
  const [showFinishConfirmDialog, setShowFinishConfirmDialog] = useState(false)
  const [showFinishSecondConfirmDialog, setShowFinishSecondConfirmDialog] = useState(false)
  const [selectedUnassignedPlayerIds, setSelectedUnassignedPlayerIds] = useState<number[]>([])
  const [selectedEmptyTableMatchId, setSelectedEmptyTableMatchId] = useState("")
  const [showMyTableModal, setShowMyTableModal] = useState(false)

  // Check if user is store owner or organizer
  const isStoreOwner =
    userInfo?.User?.Person?.Store && userInfo.User.Person.Store.id === tournament?.Store.id

  const newRounds = tournament?.rounds > round?.sortOrder

  const isOrganizer = organizers?.some(({ personId }) => personId === userInfo?.User?.Person?.id)

  const canManageTournament = isAdmin || isStoreOwner || isOrganizer
  const canManageTableActions = isAdmin || isStoreOwner
  // nacional = 6, premier = 4
  const isPremierOrNacionalTournament = [4, 6].includes(tournament?.typeId)
  const canModifyReportedResults = !isPremierOrNacionalTournament || isStoreOwner

  // El código de jugador es información privada: solo lo ven admins,
  // dueños de tienda y organizadores del torneo. Cualquier otro visitante
  // (incluida la vista anónima) nunca debe verlo, sin importar el estado
  // del torneo.
  const canSeeCode = canManageTournament

  // Mesa del jugador logeado (o identificado por código, si no hay sesión)
  // en esta ronda, si participa en ella.
  const [manualPersonId, setManualPersonId] = useState<number | null>(() =>
    getStoredPersonId(Number(tournamentId)),
  )
  const [showCodePrompt, setShowCodePrompt] = useState(false)
  const [codeInput, setCodeInput] = useState("")
  const myPersonId = userInfo?.User?.Person?.id ?? manualPersonId ?? undefined

  const getMatchForPersonId = (personId: number | undefined | null) => {
    if (!personId || !round?.TournamentMatches) return null
    return (
      round.TournamentMatches.find(
        (m) => m.Player?.Person?.id === personId || m.Opponent?.Person?.id === personId,
      ) ?? null
    )
  }

  const myMatch = getMatchForPersonId(myPersonId)

  const myMatchOpponent =
    myMatch && myMatch.Player?.Person?.id === myPersonId ? myMatch.Opponent : myMatch?.Player

  const myMatchResult = useMemo(() => {
    if (!myMatch || !myMatch.reported) return null
    if (myMatch.draw) return { label: "Empate", color: "text-yellow-600 dark:text-yellow-400" }
    if (!myMatch.Opponent) return { label: "Bye", color: "text-blue-600 dark:text-blue-400" }

    const isWinner =
      (myMatch.Player?.Person?.id === myPersonId && myMatch.winnerId === myMatch.playerId) ||
      (myMatch.Opponent?.Person?.id === myPersonId && myMatch.winnerId === myMatch.opponentId)

    return isWinner
      ? {
          label: `Ganaste ${myMatch.winnerVictories ?? ""}–${myMatch.loserVictories ?? ""}`,
          color: "text-green-600 dark:text-green-400",
        }
      : {
          label: `Perdiste ${myMatch.loserVictories ?? ""}–${myMatch.winnerVictories ?? ""}`,
          color: "text-red-600 dark:text-red-400",
        }
  }, [myMatch, myPersonId])

  const openMyTableModal = (personId: number) => {
    const match = getMatchForPersonId(personId)
    if (!match) {
      toast({
        title: "No participas en esta ronda",
        description: "No encontramos una mesa asignada para ti en esta ronda.",
        variant: "destructive",
      })
      return
    }
    setManualPersonId(personId)
    setShowMyTableModal(true)
  }

  const openCodePrompt = () => {
    setCodeInput("")
    setShowMyTableModal(false)
    setShowCodePrompt(true)
  }

  const handleShowMyTable = () => {
    if (userInfo?.User?.Person?.id) {
      openMyTableModal(userInfo.User.Person.id)
      return
    }

    // Sin sesión: ¿ya nos identificamos antes para este torneo?
    if (manualPersonId) {
      openMyTableModal(manualPersonId)
      return
    }

    openCodePrompt()
  }

  const handleSubmitCode = async () => {
    if (!tournamentId) return

    const normalizedCode = normalizePlayerCodeInput(codeInput)
    if (!PLAYER_CODE_PATTERN.test(normalizedCode)) {
      toast({
        title: "Código inválido",
        description:
          "El código de jugador debe tener el formato de 2 letras seguidas de números (ej. CL001).",
        variant: "destructive",
      })
      return
    }

    const result = await findPlayerByCode(Number(tournamentId), normalizedCode)
    if (!result) {
      toast({
        title: "Código no encontrado",
        description: "No encontramos un jugador con ese código en este torneo.",
        variant: "destructive",
      })
      return
    }

    setStoredPersonId(Number(tournamentId), result.personId)
    setManualPersonId(result.personId)
    setShowCodePrompt(false)
    openMyTableModal(result.personId)
  }

  const handleClearStoredCode = () => {
    if (tournamentId) clearStoredPersonId(Number(tournamentId))
    setManualPersonId(null)
    setCodeInput("")
  }

  // Jugadores sin mesa: todos los jugadores activos del torneo que no están asignados a ninguna mesa (table > 0)
  const playersWithoutTable = useMemo(() => {
    if (!tournamentPlayers || !round?.TournamentMatches) return []

    const assignedPlayerIds = new Set(
      round.TournamentMatches.filter((m) => m.table > 0).flatMap((m) =>
        [m.playerId, m.opponentId].filter(Boolean),
      ),
    )

    return tournamentPlayers
      .filter((p) => !p.drop && !assignedPlayerIds.has(p.id))
      .map((p) => {
        const sourceMatch = round.TournamentMatches?.find(
          (m) => m.playerId === p.id || m.opponentId === p.id,
        )
        return {
          sourceMatchId: sourceMatch?.id ?? 0,
          playerId: p.id,
          name: p.Person?.fullName || `Jugador ${p.id}`,
          code: canSeeCode ? p.Person?.code || "" : "",
        }
      })
  }, [tournamentPlayers, round?.TournamentMatches, canSeeCode])

  // Mesas vacías disponibles para asociar jugadores sin mesa: incluye mesas
  // ya liberadas (existen como TournamentMatch sin jugadores) y mesas
  // "faltantes" (aún no tienen fila creada) hasta cubrir la cantidad mínima
  // de mesas que la ronda debería tener según los jugadores activos.
  // Se identifican con un id negativo (-table) para distinguirlas de mesas
  // reales al momento de asociar jugadores.
  const emptyTableMatches = useMemo(() => {
    const matches = round?.TournamentMatches || []

    const realEmptyTables = matches.filter(
      (match) => match.table > 0 && !match.playerId && !match.opponentId,
    )

    const activePlayers = (tournamentPlayers || []).filter((p) => !p.drop).length
    const requiredTables = Math.ceil(activePlayers / 2)

    const existingTableNumbers = new Set(matches.map((match) => match.table))

    const missingTables = []
    for (let table = 1; table <= requiredTables; table += 1) {
      if (!existingTableNumbers.has(table)) {
        missingTables.push({ id: -table, table, virtual: true })
      }
    }

    return [...realEmptyTables, ...missingTables].sort((a, b) => a.table - b.table)
  }, [round?.TournamentMatches, tournamentPlayers])

  // If this is a PDF print route, apply print styles
  const isPrintMode = pdf === "pdf" || tables

  if (isPrintMode) {
    if (loading) {
      return <LoadingScreen message="Cargando vista de impresión..." />
    }

    if (!round) {
      return <div className="p-4">Error: No se pudo cargar la ronda</div>
    }

    return (
      <MatchTablePrint
        matches={round.TournamentMatches || []}
        roundName={round.name}
        tournamentName={tournament?.name || ""}
        canSeeCode={canSeeCode}
      />
    )
  }

  const handleReportResult = (matchId: number) => {
    const match = round?.TournamentMatches?.find((m) => m.id === matchId)
    if (match?.reported && !canModifyReportedResults) {
      toast({
        title: "Acción restringida",
        description:
          "En torneos Premier o Nacional, solo el administrador o el dueño de la tienda pueden modificar resultados.",
        variant: "destructive",
      })
      return
    }

    if (match) {
      setSelectedMatch(match)
    }
  }

  const handlePrintMatches = () => {
    const printUrl = `/tournament/round/${roundId}/${slug}/pdf/${tournamentId}`
    window.open(printUrl, "_blank")
  }

  const handleStartNextRound = async ({ addNew = true }) => {
    if (!tournament) return

    try {
      const result = await roundStart({
        tournamentId: tournament.id,
        addNew,
        earlyClose: false,
      })

      if (result?.status?.success && result?.Round?.urlView) {
        if (addNew) {
          toast({
            title: "Ronda en curso",
            variant: "success",
            description: "La siguiente ronda ha sido iniciada exitosamente",
          })
          window.open(result.Round.urlView + `/${tournament.id}`, "_self")
        } else {
          toast({
            title: "Torneo finalizado",
            variant: "success",
            description: "El torneo ha terminado, revisa los detalles del evento.",
          })
          window.open(result.Round.urlStanding + `/${tournament.id}`, "_self")
        }
      } else {
        toast({
          title: "Error",
          description: result?.status?.detail || "No se pudo iniciar la ronda",
          variant: "destructive",
        })
      }
    } catch {
      toast({
        title: "Error",
        description: "Error al iniciar la siguiente ronda",
        variant: "destructive",
      })
    }
  }

  const handleClearResults = async () => {
    if (!tournament) return

    try {
      const result = await roundReset({
        tournamentId: tournament.id,
        roundId: round.id,
        resetType: 3,
      })

      if (result?.status?.success) {
        toast({
          title: "Resultados limpiados",
          variant: "success",
          description: "Todos los resultados han sido eliminados",
        })
        refetchRound() // Refetch round data
      } else {
        toast({
          title: "Error",
          description: result?.status?.detail || "No se pudieron limpiar los resultados",
          variant: "destructive",
        })
      }
    } catch {
      toast({
        title: "Error",
        description: "Error al limpiar los resultados",
        variant: "destructive",
      })
    }
  }

  const handleReleaseTable = async (matchId: number) => {
    if (!canManageTableActions) {
      toast({
        title: "Sin permisos",
        description: "Solo el administrador o el dueño de la tienda pueden liberar mesas.",
        variant: "destructive",
      })
      return
    }

    if (!round || !tournament) return

    const targetMatch = round.TournamentMatches?.find((match) => match.id === matchId)
    if (!targetMatch) return

    const playersToMove = [targetMatch.playerId, targetMatch.opponentId].filter(
      (playerId) => !!playerId,
    )

    if (!playersToMove.length) {
      toast({
        title: "Sin jugadores",
        description: "La mesa ya se encuentra liberada.",
        variant: "destructive",
      })
      return
    }

    const emptyNoTableSlots = (round.TournamentMatches || []).filter(
      (match) =>
        match.id !== matchId &&
        match.table === 0 &&
        !match.playerId &&
        !match.opponentId &&
        !match.reported,
    )

    try {
      let movedPlayers = 0

      for (let index = 0; index < playersToMove.length; index += 1) {
        const playerId = playersToMove[index]
        const slot = emptyNoTableSlots[index]
        if (!slot) {
          continue
        }
        const result = await tournamentMatchUpdate({
          id: slot.id,
          tournamentId: tournament.id,
          playerId,
          opponentId: 0,
          reset: true,
        })
        if (!result?.status?.success) {
          throw new Error(result?.status?.detail || "No se pudo mover un jugador sin mesa")
        }
        movedPlayers += 1
      }

      const releaseResult = await tournamentMatchUpdate({
        id: matchId,
        tournamentId: tournament.id,
        playerId: 0,
        opponentId: 0,
        reset: true,
      })

      if (!releaseResult?.status?.success) {
        throw new Error(releaseResult?.status?.detail || "No se pudo liberar la mesa")
      }

      toast({
        title: "Mesa liberada",
        description:
          movedPlayers === playersToMove.length
            ? "Los jugadores pasaron a estado sin mesa y la mesa quedó vacía."
            : "La mesa quedó vacía.",
        variant: "success",
      })
      refetchRound()
    } catch {
      toast({
        title: "Error",
        description: "No se pudo liberar la mesa.",
        variant: "destructive",
      })
    }
  }

  const handleAssignPlayersToTable = async ({
    targetMatchId,
    playerOneSourceMatchId,
    playerOneId,
    playerTwoSourceMatchId,
    playerTwoId,
  }: {
    targetMatchId: number
    playerOneSourceMatchId: number
    playerOneId: number
    playerTwoSourceMatchId?: number
    playerTwoId?: number
  }) => {
    if (!canManageTableActions) {
      toast({
        title: "Sin permisos",
        description: "Solo el administrador o el dueño de la tienda pueden asociar jugadores.",
        variant: "destructive",
      })
      return
    }

    if (!tournament || !round) return

    // Mesa "faltante" (id negativo = -número de mesa): aún no existe como
    // fila en la base de datos, hay que crearla en vez de actualizarla.
    if (targetMatchId < 0) {
      if (!playerOneId) {
        toast({
          title: "Datos incompletos",
          description: "Selecciona al menos un jugador para asociar la mesa.",
          variant: "destructive",
        })
        return
      }

      try {
        const createResult = await tournamentMatchCreate({
          tournamentId: tournament.id,
          roundId: round.id,
          table: -targetMatchId,
          playerId: playerOneId,
          opponentId: playerTwoId ?? 0,
        })

        if (!createResult?.status?.success) {
          throw new Error(createResult?.status?.detail || "No se pudo crear la mesa")
        }

        const sourceMatchIds = Array.from(
          new Set(
            [playerOneSourceMatchId, playerTwoSourceMatchId].filter(
              (id): id is number => typeof id === "number" && id > 0,
            ),
          ),
        )

        for (const sourceMatchId of sourceMatchIds) {
          const clearResult = await tournamentMatchUpdate({
            id: sourceMatchId,
            tournamentId: tournament.id,
            playerId: 0,
            opponentId: 0,
            reset: true,
          })
          if (!clearResult?.status?.success) {
            throw new Error(clearResult?.status?.detail || "No se pudo limpiar una fila sin mesa")
          }
        }

        toast({
          title: "Mesa creada",
          description: "Se creó una mesa nueva y se asociaron los jugadores.",
          variant: "success",
        })
        refetchRound()
      } catch {
        toast({
          title: "Error",
          description: "No se pudo crear la mesa para asociar jugadores.",
          variant: "destructive",
        })
      }
      return
    }

    const targetMatch = round.TournamentMatches?.find((match) => match.id === targetMatchId)
    if (!targetMatch) {
      toast({
        title: "Error",
        description: "No se encontró la mesa objetivo.",
        variant: "destructive",
      })
      return
    }

    const targetHasPlayerOnly = !!targetMatch.playerId && !targetMatch.opponentId
    const targetIsEmpty = !targetMatch.playerId && !targetMatch.opponentId

    if (!targetHasPlayerOnly && !targetIsEmpty) {
      toast({
        title: "Mesa completa",
        description: "Esta mesa ya tiene dos jugadores asignados.",
        variant: "destructive",
      })
      return
    }

    const assignPlayerId = targetHasPlayerOnly ? targetMatch.playerId : playerOneId
    const assignOpponentId = targetHasPlayerOnly ? playerOneId : (playerTwoId ?? 0)

    if (!assignPlayerId) {
      toast({
        title: "Datos incompletos",
        description: "Selecciona al menos un jugador para asociar la mesa.",
        variant: "destructive",
      })
      return
    }

    try {
      const assignResult = await tournamentMatchUpdate({
        id: targetMatchId,
        tournamentId: tournament.id,
        playerId: assignPlayerId,
        opponentId: assignOpponentId,
        reset: true,
      })

      if (!assignResult?.status?.success) {
        throw new Error(assignResult?.status?.detail || "No se pudo asociar la mesa")
      }

      const sourceMatchIds = Array.from(
        new Set(
          [playerOneSourceMatchId, playerTwoSourceMatchId].filter(
            (id): id is number => typeof id === "number" && id > 0 && id !== targetMatchId,
          ),
        ),
      )

      for (const sourceMatchId of sourceMatchIds) {
        const clearResult = await tournamentMatchUpdate({
          id: sourceMatchId,
          tournamentId: tournament.id,
          playerId: 0,
          opponentId: 0,
          reset: true,
        })
        if (!clearResult?.status?.success) {
          throw new Error(clearResult?.status?.detail || "No se pudo limpiar una fila sin mesa")
        }
      }

      toast({
        title: "Mesa actualizada",
        description:
          assignOpponentId > 0
            ? "Se asociaron jugadores sin mesa a la mesa seleccionada."
            : "Se asoció un jugador a la mesa. Puedes completar el segundo jugador después.",
        variant: "success",
      })
      refetchRound()
    } catch {
      toast({
        title: "Error",
        description: "No se pudo asociar jugadores a la mesa.",
        variant: "destructive",
      })
    }
  }

  const handleToggleUnassignedPlayer = (playerId: number) => {
    setSelectedUnassignedPlayerIds((prev) => {
      if (prev.includes(playerId)) {
        return prev.filter((id) => id !== playerId)
      }
      if (prev.length >= 2) {
        toast({
          title: "Límite alcanzado",
          description: "Solo puedes seleccionar hasta 2 jugadores para asociar a una mesa.",
          variant: "destructive",
        })
        return prev
      }
      return [...prev, playerId]
    })
  }

  const handleAssignSelectedUnassignedPlayers = async () => {
    if (!selectedEmptyTableMatchId || !tournament) return
    if (!selectedUnassignedPlayerIds.length) {
      toast({
        title: "Selecciona jugadores",
        description: "Debes seleccionar al menos un jugador sin mesa.",
        variant: "destructive",
      })
      return
    }

    const playerOne = playersWithoutTable.find((p) => p.playerId === selectedUnassignedPlayerIds[0])
    const playerTwo =
      selectedUnassignedPlayerIds.length > 1
        ? playersWithoutTable.find((p) => p.playerId === selectedUnassignedPlayerIds[1])
        : undefined

    if (!playerOne) {
      toast({
        title: "Error",
        description: "No se encontró el jugador seleccionado.",
        variant: "destructive",
      })
      return
    }

    await handleAssignPlayersToTable({
      targetMatchId: Number(selectedEmptyTableMatchId),
      playerOneSourceMatchId: playerOne.sourceMatchId,
      playerOneId: playerOne.playerId,
      playerTwoSourceMatchId: playerTwo?.sourceMatchId,
      playerTwoId: playerTwo?.playerId,
    })

    setSelectedUnassignedPlayerIds([])
    setSelectedEmptyTableMatchId("")
  }

  if (loading || loadingTournament || loadingOrganizers) {
    return (
      <Layout>
        <LoadingScreen message="Cargando ronda..." />
      </Layout>
    )
  }

  if (error || !round) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>No se pudo cargar la información de la ronda.</AlertDescription>
          </Alert>
        </div>
      </Layout>
    )
  }

  const allMatchesReported = round.TournamentMatches?.every((match) => match.reported) || false

  const someReported =
    round.TournamentMatches?.filter((x) => x.opponentId).some((match) => match.reported) || false

  const hasMatches = round.TournamentMatches && round.TournamentMatches.length > 0

  const isRoundInProgress = round.statusId === 1
  const canManageTables = !!canManageTableActions && (isRoundInProgress || allMatchesReported)
  const canReleaseTables = !!canManageTableActions && isRoundInProgress

  return (
    <Layout>
      <HeaderTournament tournament={tournament} round={round} />
      <div className="container mx-auto px-0 py-0">
        <TournamentNavigation
          tournament={tournament}
          tournamentName={tournament?.name || ""}
          currentPage="rounds"
          tournamentStatusId={tournament?.statusId}
          showRounds
        />

        {/* Matches */}
        {hasMatches ? (
          <Card className="mb-6">
            <CardHeader className="p-3">
              <CardTitle className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Trophy className="h-5 w-5" />
                  Enfrentamientos ({round.TournamentMatches.length})
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShowMyTable}
                  className="shadow-md rounded-xl"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Mostrar mi mesa
                </Button>
                {canManageTournament && [1, 2, 3, 4].includes(tournament.statusId) && (
                  <div className="flex flex-col w-full md:w-auto md:flex-row md:items-center gap-2 md:gap-3">
                    {isRoundInProgress && someReported && (isAdmin || isStoreOwner) && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={resettingRound}
                          onClick={() => setShowResetConfirmDialog(true)}
                          className="w-full md:w-auto shadow-md rounded-xl"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Limpiar Resultados
                        </Button>

                        <AlertDialog
                          open={showResetConfirmDialog}
                          onOpenChange={setShowResetConfirmDialog}
                        >
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Estás seguro?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción eliminará todos los resultados reportados de la ronda "
                                {round.name}". Esta acción no se puede deshacer.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => {
                                  setShowResetConfirmDialog(false)
                                  setShowResetSecondConfirmDialog(true)
                                }}
                              >
                                Continuar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog
                          open={showResetSecondConfirmDialog}
                          onOpenChange={setShowResetSecondConfirmDialog}
                        >
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmación final</AlertDialogTitle>
                              <AlertDialogDescription>
                                Vas a limpiar todos los resultados de la ronda. Confirma solo si
                                deseas reiniciar los reportes.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Mantener Resultados</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  setShowResetSecondConfirmDialog(false)
                                  await handleClearResults()
                                }}
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                              >
                                Sí, Limpiar Todo
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}

                    {isRoundInProgress && allMatchesReported && newRounds && (
                      <Button
                        onClick={() => handleStartNextRound({ addNew: true })}
                        disabled={!allMatchesReported || startingRound}
                        className="w-full md:w-auto shadow-md rounded-xl"
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {startingRound ? "Iniciando..." : "Iniciar Siguiente Ronda"}
                      </Button>
                    )}
                    {isRoundInProgress && allMatchesReported && tournament.statusId === 4 && (
                      <>
                        <Button
                          onClick={() => setShowFinishConfirmDialog(true)}
                          disabled={!allMatchesReported || startingRound}
                          className="w-full md:w-auto shadow-md rounded-xl"
                        >
                          <Play className="h-4 w-4 mr-2" />
                          {startingRound ? "Finalizando..." : "Finalizar Torneo"}
                        </Button>

                        <AlertDialog
                          open={showFinishConfirmDialog}
                          onOpenChange={setShowFinishConfirmDialog}
                        >
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Finalizar torneo?</AlertDialogTitle>
                              <AlertDialogDescription>
                                El torneo pasará a estado final y se mostrará la clasificación
                                definitiva.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => {
                                  setShowFinishConfirmDialog(false)
                                  setShowFinishSecondConfirmDialog(true)
                                }}
                              >
                                Continuar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>

                        <AlertDialog
                          open={showFinishSecondConfirmDialog}
                          onOpenChange={setShowFinishSecondConfirmDialog}
                        >
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Confirmación final</AlertDialogTitle>
                              <AlertDialogDescription>
                                Esta acción cierra el torneo y no debería usarse por error. ¿Deseas
                                finalizarlo ahora?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Volver</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={async () => {
                                  setShowFinishSecondConfirmDialog(false)
                                  await handleStartNextRound({ addNew: false })
                                }}
                              >
                                Sí, Finalizar Torneo
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </>
                    )}
                  </div>
                )}
              </CardTitle>
            </CardHeader>

            <CardContent className="p-2">
              <MatchTable
                matches={round.TournamentMatches}
                onReportResult={handleReportResult}
                showOnlyPending={showOnlyPending}
                onShowOnlyPendingChange={setShowOnlyPending}
                canReport={isRoundInProgress && !!canManageTournament}
                canModifyReportedResults={canModifyReportedResults}
                onPrintMatches={handlePrintMatches}
                isAdmin={isAdmin}
                canManageTables={canManageTables}
                canReleaseTables={canReleaseTables}
                isUpdatingTables={updatingMatchTables}
                onReleaseTable={handleReleaseTable}
                onAssignPlayersToTable={handleAssignPlayersToTable}
                playersWithoutTable={playersWithoutTable}
                canSeeCode={canSeeCode}
              />

              {allMatchesReported && round.statusId === 1 && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Todos los resultados han sido reportados. Puedes iniciar la siguiente ronda.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-muted-foreground">No hay enfrentamientos para esta ronda.</p>
            </CardContent>
          </Card>
        )}

        <Dialog open={showMyTableModal} onOpenChange={setShowMyTableModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Table2 className="h-5 w-5" />
                {myMatch ? `Mesa ${myMatch.table}` : "Tu mesa"}
              </DialogTitle>
              <DialogDescription>
                {myMatch
                  ? "Esta es tu mesa asignada en esta ronda."
                  : "No encontramos una mesa asignada para ti en esta ronda."}
              </DialogDescription>
            </DialogHeader>

            {myMatch && (
              <div className="space-y-4">
                <div className="flex items-center justify-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-3xl font-bold text-primary">
                    {myMatch.table}
                  </div>
                </div>

                {myMatchOpponent ? (
                  <div className="flex items-center justify-center gap-3 rounded-xl border p-3">
                    <UserAvatar
                      src={myMatchOpponent.Person?.urlAvatar}
                      name={myMatchOpponent.Person?.fullName}
                      size="md"
                    />
                    <div>
                      <p className="text-xs text-muted-foreground">Rival</p>
                      <p className="font-medium">{myMatchOpponent.Person?.fullName}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-sm text-muted-foreground">
                    Bye — no tienes rival en esta ronda.
                  </p>
                )}

                {myMatchResult && (
                  <p className={`text-center text-sm font-semibold ${myMatchResult.color}`}>
                    {myMatchResult.label}
                  </p>
                )}
              </div>
            )}

            {!isAuthenticated && (
              <Button variant="ghost" size="sm" onClick={openCodePrompt} className="w-full">
                Buscar otro resultado
              </Button>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showCodePrompt} onOpenChange={setShowCodePrompt}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>¿Cuál es tu código de jugador?</DialogTitle>
              <DialogDescription>
                No detectamos una sesión iniciada. Ingresa tu código de jugador para buscar tu mesa
                en este torneo. Lo guardaremos en este navegador para futuras rondas.
              </DialogDescription>
            </DialogHeader>

            {manualPersonId && (
              <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <span>Ya te identificamos en este torneo.</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearStoredCode}
                  className="h-auto p-1 text-muted-foreground hover:text-destructive"
                >
                  Olvidar
                </Button>
              </div>
            )}

            <Input
              placeholder="Ej: CL001"
              value={codeInput}
              onChange={(e) => setCodeInput(normalizePlayerCodeInput(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSubmitCode()
              }}
              autoFocus
            />

            <Button
              onClick={handleSubmitCode}
              disabled={!PLAYER_CODE_PATTERN.test(codeInput) || findingPlayerByCode}
              className="w-full"
            >
              {findingPlayerByCode ? "Buscando..." : "Buscar mi mesa"}
            </Button>
          </DialogContent>
        </Dialog>

        {/* Players without table */}
        {canManageTables && playersWithoutTable.length > 0 && (
          <Card className="mb-6 border-yellow-400 dark:border-yellow-500">
            <CardHeader className="p-3">
              <CardTitle className="flex items-center gap-2 text-yellow-600 dark:text-yellow-400">
                <Users className="h-5 w-5" />
                Jugadores sin mesa ({playersWithoutTable.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              <div className="mb-4 grid gap-2 md:grid-cols-[1fr_auto] md:items-center">
                <Select
                  value={selectedEmptyTableMatchId}
                  onValueChange={setSelectedEmptyTableMatchId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar mesa vacía" />
                  </SelectTrigger>
                  <SelectContent>
                    {emptyTableMatches.map((match) => (
                      <SelectItem key={match.id} value={String(match.id)}>
                        Mesa #{match.table}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  disabled={
                    updatingMatchTables ||
                    creatingMatchTable ||
                    !selectedEmptyTableMatchId ||
                    selectedUnassignedPlayerIds.length === 0
                  }
                  onClick={handleAssignSelectedUnassignedPlayers}
                >
                  Asociar seleccionados
                </Button>
              </div>

              <div className="flex flex-wrap gap-2">
                {playersWithoutTable.map((player) => (
                  <button
                    type="button"
                    onClick={() => handleToggleUnassignedPlayer(player.playerId)}
                    key={player.playerId}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-colors ${
                      selectedUnassignedPlayerIds.includes(player.playerId)
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950"
                        : "border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-950"
                    }`}
                  >
                    <span className="font-medium">{player.name}</span>
                    {player.code && (
                      <span className="text-muted-foreground text-xs">({player.code})</span>
                    )}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Match Report Modal */}
        <MatchReportModal
          match={selectedMatch}
          tournamentId={tournament?.id || 0}
          onClose={() => setSelectedMatch(null)}
          onSuccess={() => refetchRound()}
          roundId={round.id}
        />
      </div>
    </Layout>
  )
}

export default TournamentRoundView
